// src/datablog/DataBlog.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getSortedPosts, getAllCategories, getAllTags, searchPosts, filterPostsByCategory, filterPostsByTag } from './posts/posts';
import './DataBlog.css';
import { Helmet } from 'react-helmet-async';

const DataBlog = () => {
  // Get search params for direct links to filtered views
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedTag, setSelectedTag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(6);
  const [metaTitle, setMetaTitle] = useState('Data Visualization Blog | Chart2Video');
  const [metaDescription, setMetaDescription] = useState('Explore our articles on data visualization techniques, MS Word charts, and animated chart creation to enhance your presentations and content.');

  // Initialize data and handle any URL parameters
  useEffect(() => {
    // Get all data
    const allPosts = getSortedPosts();
    const allCategories = getAllCategories();
    const allTags = getAllTags();
    
    // Set initial state
    setCategories(['All Categories', ...allCategories]);
    setTags(allTags);
    
    // Check URL params for filters
    const categoryParam = searchParams.get('category');
    const tagParam = searchParams.get('tag');
    const searchParam = searchParams.get('search');
    
    // Apply filters from URL if present
    let filteredPosts = allPosts;
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      filteredPosts = filterPostsByCategory(categoryParam);
      setMetaTitle(`${categoryParam} - Data Visualization Blog | Chart2Video`);
      setMetaDescription(`Explore our ${categoryParam} articles for insights on data visualization techniques and chart creation.`);
    }
    
    if (tagParam) {
      setSelectedTag(tagParam);
      filteredPosts = filterPostsByTag(tagParam);
      setMetaTitle(`${tagParam} - Data Visualization Blog | Chart2Video`);
      setMetaDescription(`Explore articles tagged with ${tagParam} for insights on data visualization and chart creation.`);
    }
    
    if (searchParam) {
      setSearchQuery(searchParam);
      filteredPosts = searchPosts(searchParam);
      setMetaTitle(`Search: ${searchParam} | Chart2Video Blog`);
      setMetaDescription(`Search results for "${searchParam}" - Find insights on data visualization and chart creation.`);
    }
    
    // Set filtered posts
    setPosts(filteredPosts);
    
  }, [searchParams]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Update URL with search parameter
    if (query) {
      searchParams.set('search', query);
      if (selectedCategory !== 'All Categories') searchParams.set('category', selectedCategory);
      if (selectedTag) searchParams.set('tag', selectedTag);
    } else {
      searchParams.delete('search');
    }
    setSearchParams(searchParams);
    
    let filteredPosts = searchPosts(query);
    
    // Apply category filter if one is selected
    if (selectedCategory !== 'All Categories') {
      filteredPosts = filteredPosts.filter(post => 
        post.categories && post.categories.includes(selectedCategory)
      );
    }
    
    // Apply tag filter if one is selected
    if (selectedTag) {
      filteredPosts = filteredPosts.filter(post => 
        post.tags && post.tags.includes(selectedTag)
      );
    }
    
    setPosts(filteredPosts);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle category filter
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    
    // Update URL with category parameter
    if (category !== 'All Categories') {
      searchParams.set('category', category);
      if (selectedTag) searchParams.set('tag', selectedTag);
      if (searchQuery) searchParams.set('search', searchQuery);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
    
    let filteredPosts = filterPostsByCategory(category);
    
    // Apply search query if there is one
    if (searchQuery) {
      filteredPosts = filteredPosts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply tag filter if one is selected
    if (selectedTag) {
      filteredPosts = filteredPosts.filter(post => 
        post.tags && post.tags.includes(selectedTag)
      );
    }
    
    setPosts(filteredPosts);
    setCurrentPage(1); // Reset to first page when changing category
    
    // Update meta title and description for SEO
    if (category !== 'All Categories') {
      setMetaTitle(`${category} - Data Visualization Blog | Chart2Video`);
      setMetaDescription(`Explore our ${category} articles for insights on data visualization techniques and chart creation.`);
    } else {
      setMetaTitle('Data Visualization Blog | Chart2Video');
      setMetaDescription('Explore our articles on data visualization techniques and animated chart creation.');
    }
  };

  // Handle tag selection
  const handleTagClick = (tag) => {
    // If the same tag is clicked, clear the filter
    const newTag = tag === selectedTag ? '' : tag;
    setSelectedTag(newTag);
    
    // Update URL with tag parameter
    if (newTag) {
      searchParams.set('tag', newTag);
      if (selectedCategory !== 'All Categories') searchParams.set('category', selectedCategory);
      if (searchQuery) searchParams.set('search', searchQuery);
    } else {
      searchParams.delete('tag');
    }
    setSearchParams(searchParams);
    
    let filteredPosts;
    
    if (newTag === '') {
      // Just apply category filter
      filteredPosts = filterPostsByCategory(selectedCategory);
    } else {
      // Apply both filters
      filteredPosts = filterPostsByTag(newTag);
      
      if (selectedCategory !== 'All Categories') {
        filteredPosts = filteredPosts.filter(post => 
          post.categories && post.categories.includes(selectedCategory)
        );
      }
    }
    
    // Apply search query if there is one
    if (searchQuery) {
      filteredPosts = filteredPosts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setPosts(filteredPosts);
    setCurrentPage(1); // Reset to first page when selecting a tag
    
    // Update meta title and description for SEO
    if (newTag) {
      setMetaTitle(`${newTag} - Data Visualization Blog | Chart2Video`);
      setMetaDescription(`Explore articles tagged with ${newTag} for insights on data visualization and chart creation.`);
    } else if (selectedCategory !== 'All Categories') {
      setMetaTitle(`${selectedCategory} - Data Visualization Blog | Chart2Video`);
      setMetaDescription(`Explore our ${selectedCategory} articles for insights on data visualization techniques and chart creation.`);
    } else {
      setMetaTitle('Data Visualization Blog | Chart2Video');
      setMetaDescription('Explore our articles on data visualization techniques and animated chart creation.');
    }
  };

  // Get current posts for pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  // Create structured data for blog page
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Chart2Video DataBlog",
    "description": "Insights on data visualization techniques and animated chart creation",
    "url": "https://www.chart2video.com/datablog",
    "publisher": {
      "@type": "Organization",
      "name": "Chart2Video",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.chart2video.com/logo.png"
      }
    },
    "blogPost": posts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.summary,
      "url": `https://www.chart2video.com/datablog/${post.slug}`,
      "datePublished": post.date,
      "dateModified": post.updatedDate || post.date,
      "image": `https://www.chart2video.com${post.image}`,
      "author": {
        "@type": "Organization",
        "name": post.author || "Chart2Video Team"
      }
    }))
  };

  return (
    <div className="datablog-container">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content="https://www.chart2video.com/images/datablog-thumbnail.jpg" />
        <meta property="og:url" content="https://www.chart2video.com/datablog" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://www.chart2video.com/images/datablog-thumbnail.jpg" />
        

        {/* In future if we change to more pages in datablog blog we should do something like " <link rel="canonical" href={`https://www.chart2video.com/datablog${selectedCategory !== 'All Categories' ? '?category=' + selectedCategory : ''}${selectedTag ? '?tag=' + selectedTag : ''}`} />" */}
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.chart2video.com/datablog" />
        
        {/* Structured data for blog page */}
        <script type="application/ld+json">
          {JSON.stringify(blogStructuredData)}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="datablog-hero">
        <div className="datablog-hero-content">
          <h1>DataBlog</h1>
          <p>Insights, tutorials, and best practices for data visualization and chart animations</p>
        </div>
      </section>
      
      {/* Search and Filters */}
      <div className="datablog-search-container">
        <div className="datablog-search-bar">
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={handleSearch}
            className="datablog-search-input"
            aria-label="Search articles"
          />
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="datablog-category-select"
            aria-label="Filter by category"
          >
            {categories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        {/* Tags Filter */}
        {tags.length > 0 && (
          <div className="datablog-tags-container">
            <div className="datablog-tags-label">Popular Tags:</div>
            <div className="datablog-tags" role="list">
              {tags.slice(0, 10).map((tag, index) => (
                <button
                  key={index}
                  className={`datablog-tag ${selectedTag === tag ? 'active' : ''}`}
                  onClick={() => handleTagClick(tag)}
                  aria-label={`Filter by tag: ${tag}`}
                  role="listitem"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Posts Grid */}
      {currentPosts.length === 0 ? (
        <div className="datablog-no-posts">
          <p>No posts found matching your criteria. Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="datablog-posts-grid" role="feed" aria-label="Blog posts">
          {currentPosts.map((post) => (
            <article key={post.slug} className="datablog-post-card">
              <Link to={`/datablog/${post.slug}`} className="datablog-post-link">
                <div className="datablog-post-image">
                  <img 
                    src={post.image} 
                    alt={post.imageAlt || `Thumbnail for ${post.title}`}
                    width="350" 
                    height="200" 
                    loading="lazy"
                  />
                  {post.categories && post.categories.length > 0 && (
                    <div className="datablog-post-category">{post.categories[0]}</div>
                  )}
                </div>
                <div className="datablog-post-content">
                  <h2 className="datablog-post-title">{post.title}</h2>
                  <p className="datablog-post-date">{formatDate(post.date)}</p>
                  <p className="datablog-post-summary">{post.summary}</p>
                  
                  {post.tags && post.tags.length > 0 && (
                    <div className="datablog-post-tags">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="datablog-post-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            </article>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="datablog-pagination" aria-label="Blog pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="datablog-pagination-button"
            aria-label="Go to previous page"
          >
            Previous
          </button>
          
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`datablog-pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
              aria-label={`Go to page ${index + 1}`}
              aria-current={currentPage === index + 1 ? "page" : undefined}
            >
              {index + 1}
            </button>
          ))}
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="datablog-pagination-button"
            aria-label="Go to next page"
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};

export default DataBlog;